import React, { useState } from 'react';

import { InputAdornment, styled, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Button from 'components/Button';
import LoginSubmitButton from 'containers/LoginPage/components/LoginContinueButton';
import LoginPageSubtitle from 'containers/LoginPage/components/LoginPageSubtitle';
import LoginTextField from 'containers/LoginPage/components/LoginTextField';
import LoginContainerWithBackButton from 'containers/LoginPage/containers/LoginContainerWithBackButton';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import {
  useLazyCheckValidationCodeQuery,
  useSendValidationEmailMutation,
} from 'redux/api/registration.api';
import globalMessages from 'translations/messages/global-messages';

const LoginContainerWithBackButton_CodeVerification = styled(LoginContainerWithBackButton)(
  ({ theme }) => ({
    '& .login-code-verification-form': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: theme.spacing(8),
      gap: theme.spacing(4),
    },
    '& .login-resend-button': {
      position: 'absolute',
      bottom: theme.spacing(30),
      left: '50%',
      transform: 'translateX(-50%)',
    },
  }),
);

const LoginCodeVerification = ({
  email,
  code: initialCode = '',
  firstName = '',
  lastName = '',
  onCodeValidate,
  onBackButtonClick,
}) => {
  // #region [intl]
  const intl = useIntl();
  // #endregion

  // #region [states]
  const [code, setCode] = useState(initialCode);
  const [disableResendButton, setDisableResendButton] = useState(false);
  // #endregion

  // #region [redux]
  const dispatchGlobalError = useGlobalErrorHandler();
  const [
    sendValidationEmail,
    { isLoading: sendingValidationEmail },
  ] = useSendValidationEmailMutation();
  const [checkValidationCode, { isLoading: checkingCode }] = useLazyCheckValidationCodeQuery();
  // #endregion

  // #region [methods]
  const handleSubmit = async evt => {
    try {
      evt.preventDefault();
      const fconeCode = `F-${code}`;
      await checkValidationCode({ email, code: fconeCode }).unwrap();
      const userInfo = {
        email,
        exists: false,
        idp_info: null,
        connection_name: null,
        is_email_verified: true,
        email_validation_required: true,
        validationCode: fconeCode,
        deep_links_triggered: [],
        invitations: [],
        firstName,
        lastName,
      };
      onCodeValidate(userInfo);
    } catch (err) {
      dispatchGlobalError({
        error: err,
        codeToMessageIntl: {
          INCORRECT_VALIDATION_CODE: globalMessages.invalid_code_try_again,
          VALIDATION_CODE_EXPIRED: globalMessages.expired_code,
        },
      });
    }
  };

  const handleResendCodeClick = async () => {
    try {
      setDisableResendButton(true);
      await sendValidationEmail({ email }).unwrap();
      setTimeout(() => {
        setDisableResendButton(false);
      }, 30000);
    } catch (err) {
      setDisableResendButton(false);
      dispatchGlobalError({ error: err });
    }
  };
  // #endregion

  // #region [render]
  const loading = sendingValidationEmail || checkingCode;
  return (
    <LoginContainerWithBackButton_CodeVerification onBackButtonClick={onBackButtonClick}>
      <Typography variant="header" component="p">
        {intl.formatMessage(globalMessages.enter_your_code)}
      </Typography>
      <LoginPageSubtitle>
        {intl.formatMessage(globalMessages.validation_email_sent)}
      </LoginPageSubtitle>
      <form noValidate className="login-code-verification-form" onSubmit={handleSubmit}>
        <LoginTextField
          type="text"
          value={code}
          autoFocus
          placeholder={intl.formatMessage(globalMessages.type_code_here)}
          InputProps={{
            startAdornment: <InputAdornment position="start">F-</InputAdornment>,
          }}
          onChange={evt => setCode(evt.target.value)}
        />
        <LoginSubmitButton disabled={!code} loading={loading}>
          {intl.formatMessage(globalMessages.continue)}
        </LoginSubmitButton>
      </form>
      <Button
        className="login-resend-button"
        variant="plain"
        size="large"
        disabled={disableResendButton}
        onClick={handleResendCodeClick}
      >
        {intl.formatMessage(globalMessages.resend_the_email)}
      </Button>
    </LoginContainerWithBackButton_CodeVerification>
  );
  // #endregion
};

LoginCodeVerification.propTypes = {
  email: PropTypes.string.isRequired,
  code: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  onCodeValidate: PropTypes.func.isRequired,
  onBackButtonClick: PropTypes.func.isRequired,
};

export default LoginCodeVerification;
